import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { Veiculo, VeiculoEmSeuNome } from '../../ficha-veiculo/ficha-cadastro/model/veiculo';
import { ModalMessageComponent } from '../modais/modal-message/modal-message.component';
import { FichaService } from '../../ficha-veiculo/ficha-veiculo.service';

@Component({
  selector: 'app-veiculo-proprio',
  templateUrl: './veiculo-proprio.component.html',
  styleUrls: ['./veiculo-proprio.component.scss'],
})
export class VeiculoProprioComponent implements OnInit, OnDestroy {
  @Input() veiculo: VeiculoEmSeuNome;
  @Output() changeForm: EventEmitter<any> = new EventEmitter();
  @Input() veiculos: Veiculo[];
  @Input() isPlacaOpcional?: boolean;
  @Input() labelTitle?: string;

  @ViewChild('modalMessage', { static: true })
  modalMessage: ModalMessageComponent;

  form: FormGroup;

  listaTipoVeiculo = [];
  listaMarca = [];
  listaAnoModelo = [];
  listaModelo = [];
  listaVersao = [];

  isSelectedTipoVeiculo = false;
  isSelectedMarca = false;
  isSelectedAnoModelo = false;
  isSelectedModelo = false;
  isSelectedVersao = false;

  isLoadingTipoVeiculo = false;
  isLoadingMarca = false;
  isLoadingAnoModelo = false;
  isLoadingModelo = false;
  isLoadingVersao = false;

  private isAlive = false;

  private isAlteracao = false;

  private subsTipoVeiculo: Subscription;
  private subsMarca: Subscription;
  private subsModelo: Subscription;
  private subsVersao: Subscription;
  private subsForm: Subscription;
  private subsIsVeiculoEmSeuNome: Subscription;

  isSelectedTipoVeiculoAfterBlur = true;
  isSelectedMarcaAfterBlur = true;
  isSelectedAnoModeloAfterBlur = true;
  isSelectedModeloAfterBlur = true;
  isSelectedVersaoAfterBlur = true;

  constructor(
    private formBuilder: FormBuilder,
    private fichaService: FichaService
  ) {}

  ngOnInit() {
    this.buildForm();
    this.patchForm();
    this.loadListaTipoVeiculo();
    this.watchForm();
    this.initWatchIsVeiculoEmSeuNome();
  }

  ngOnDestroy() {
    if (this.subsTipoVeiculo) {
      this.subsTipoVeiculo.unsubscribe();
    }
    if (this.subsMarca) {
      this.subsMarca.unsubscribe();
    }
    if (this.subsModelo) {
      this.subsModelo.unsubscribe();
    }
    if (this.subsVersao) {
      this.subsVersao.unsubscribe();
    }
    if (this.subsForm) {
      this.subsForm.unsubscribe();
    }
    if (this.subsIsVeiculoEmSeuNome) {
      this.subsIsVeiculoEmSeuNome.unsubscribe();
    }
    this.isAlive = false;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      isVeiculoEmMeuNome: [false],
      veiculoPlaca: [
        '',
        [this.placaDuplicadaValidator(), this.requirePlacaValidator()],
      ],
      isNaoSeiPlaca: [this.isPlacaOpcional || false],
      veiculoCategoria: [{ value: null }, this.requireValidator()],
      veiculoMarca: [{ value: null, disabled: !this.isSelectedTipoVeiculo }, this.requireValidator()],
      veiculoAno: [{ value: null, disabled: !this.isSelectedMarca }, this.requireValidator()],
      veiculoModelo: [{ value: null, disabled: !this.isSelectedAnoModelo }, this.requireValidator()],
      veiculoVersao: [{ value: null, disabled: !this.isSelectedModelo }, this.requireValidator()],
    });
  }

  private requirePlacaValidator(): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      const value: VeiculoEmSeuNome = control.value;
      const valueForm = this.form && this.form.value;
      if (!valueForm) {
        return null;
      }

      if (
        !value &&
        valueForm &&
        valueForm.isVeiculoEmMeuNome &&
        !valueForm.isNaoSeiPlaca &&
        !valueForm.isPlacaOpcional
      ) {
        return { required: true };
      }

      return null;
    };
  }

  private requireValidator(): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      const value: VeiculoEmSeuNome = control.value;
      const valueForm = this.form && this.form.value;
      if (!valueForm) {
        return null;
      }

      if (
        !value &&
        valueForm &&
        valueForm.isVeiculoEmMeuNome &&
        valueForm.isNaoSeiPlaca
      ) {
        return { required: true };
      }

      return null;
    };
  }

  private placaDuplicadaValidator(): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      const value = control.value;

      if (value && this.veiculos && this.veiculos.some((v) => v.veiculoPlaca === value)) {
        return { duplicidade: true };
      }

      return null;
    };
  }

  private initWatchIsVeiculoEmSeuNome(): void {
    this.subsIsVeiculoEmSeuNome = this.form
      .get('isVeiculoEmMeuNome')
      .valueChanges.subscribe((value) => {
        this.form.get('isNaoSeiPlaca').setValue(this.isPlacaOpcional || false);
        if (!value) {
          this.form.get('veiculoPlaca').setValue('');
          this.form.get('veiculoCategoria').setValue(null);
          this.form.get('veiculoMarca').setValue(null);
          this.form.get('veiculoAno').setValue(null);
          this.form.get('veiculoModelo').setValue(null);
          this.form.get('veiculoVersao').setValue(null);
        }
      });
  }

  private patchForm(): void {
    if (this.veiculo) {
      this.form.patchValue(this.veiculo);
    }
  }

  private watchForm(): void {
    this.subsForm = this.form.valueChanges.subscribe((values) => {
      this.changeForm.emit({ values, formValid: this.formValid });
    });
  }

  loadListaTipoVeiculo() {
    this.clearList(['TipoVeiculo', 'Marca', 'AnoModelo', 'Modelo', 'Versao']);
    this.isLoadingTipoVeiculo = true;
    this.subsTipoVeiculo = this.fichaService
      .getTipoVeiculo()
      .pipe(
        take(1),
        finalize(() => (this.isLoadingTipoVeiculo = false))
      )
      .subscribe((res) => {
        if (res.tipos) {
          res.tipos.forEach((tipo) => {
            // if ([1, 2, 4, 5].includes(tipo.id)) {
            this.listaTipoVeiculo.push(tipo);
            // }
          });
        }
        this.resetAlteracao();
      });
  }

  loadListaMarca() {
    this.clearList(['Marca', 'AnoModelo', 'Modelo', 'Versao', 'Cotacao']);
    this.isLoadingMarca = true;
    this.subsMarca = this.fichaService
      .getMarcaPorTipo(this.veiculoCategoria)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingMarca = false;
        })
      )
      .subscribe((res) => {
        this.listaMarca = res.marcas;
        this.resetAlteracao();
      });
  }

  loadListaAnoModelo() {
    this.clearList(['AnoModelo', 'Modelo', 'Versao', 'Cotacao']);

    this.isLoadingAnoModelo = true;
    this.fichaService
      .getAnoModeloPorMarca(this.veiculoCategoria, this.veiculoMarca)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingAnoModelo = false;
        })
      )
      .subscribe(
        (res) => this.handleListaAnoModelo(res.anosModelo),
        (error) => {
          this.modalMessage.show(
            'Não há ano de veículo para as características selecionadas.'
          );
        }
      );
  }

  loadListaModelo() {
    this.clearList(['Modelo', 'Versao', 'Cotacao']);
    this.isLoadingModelo = true;
    this.subsModelo = this.fichaService
      .getModeloPorMarcaAno(
        this.veiculoCategoria,
        this.veiculoMarca,
        this.veiculoAno
      )
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingModelo = false;
        })
      )
      .subscribe(
        (res) => {
          this.listaModelo = res.modelos;
          this.resetAlteracao();
        },
        () =>
          this.modalMessage.show(
            ' Não há modelo de veículo para as características selecionadas.'
          )
      );
  }

  loadListaVersao() {
    this.clearList(['Versao', 'Cotacao']);
    this.isLoadingVersao = true;
    this.subsVersao = this.fichaService
      .getVersaoPorMarcaModeloAno(
        this.veiculoCategoria,
        this.veiculoMarca,
        this.veiculoAno,
        this.veiculoModelo
      )
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingVersao = false;
        })
      )
      .subscribe(
        (res) => {
          this.listaVersao = res.versoes;
          this.resetAlteracao();
        },
        (error) => {}
      );
  }

  onChangeTipoVeiculo(event) {
    this.isSelectedTipoVeiculo = true;
    this.loadListaMarca();
    this.onSelectedTipoVeiculoChange();
  }

  onChangeMarca(event) {
    this.isSelectedMarca = true;
    this.loadListaAnoModelo();
    this.onSelectedMarcaChange();
  }

  onChangeAnoModelo(event) {
    this.isSelectedAnoModelo = true;
    this.loadListaModelo();
    this.onSelectedAnoModeloChange();
  }

  onChangeModelo(event) {
    this.isSelectedModelo = true;
    this.loadListaVersao();
    this.onSelectedModeloChange();
  }

  onChangeVersao(event) {
    this.isSelectedVersao = true;
  }

  onBlurTipoVeiculo(event) {
    this.isSelectedTipoVeiculoAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurMarca(event) {
    this.isSelectedMarcaAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurAnoModelo(event) {
    this.isSelectedAnoModeloAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurModelo(event) {
    this.isSelectedModeloAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurVersao(event) {
    this.isSelectedVersaoAfterBlur = event.target.selectedIndex > 0;
  }

  private handleListaAnoModelo(anos) {
    this.listaAnoModelo = anos;
    this.resetAlteracao();
  }

  clearList(lista: string[]) {
    if (!this.isAlteracao && lista && lista.length) {
      lista.forEach((item) => {
        switch (item) {
          case 'TipoVeiculo': {
            this.listaTipoVeiculo = [];
            this.isSelectedTipoVeiculo = false;
            this.form.get('veiculoCategoria').setValue(null);
            this.onSelectedTipoVeiculoChange();
            break;
          }
          case 'Marca': {
            this.listaMarca = [];
            this.isSelectedMarca = false;
            this.form.get('veiculoMarca').setValue(null);
            this.onSelectedMarcaChange();
            break;
          }
          case 'AnoModelo': {
            this.listaAnoModelo = [];
            this.isSelectedAnoModelo = false;
            this.form.get('veiculoAno').setValue(null);
            this.onSelectedAnoModeloChange();
            break;
          }
          case 'Modelo': {
            this.listaModelo = [];
            this.isSelectedModelo = false;
            this.form.get('veiculoModelo').setValue(null);
            this.onSelectedModeloChange();
            break;
          }
          case 'Versao': {
            this.listaVersao = [];
            this.isSelectedVersao = false;
            this.form.get('veiculoVersao').setValue(null);
            break;
          }
        }
      });
      this.isSelectedTipoVeiculoAfterBlur = true;
      this.isSelectedMarcaAfterBlur = true;
      this.isSelectedAnoModeloAfterBlur = true;
      this.isSelectedModeloAfterBlur = true;
      this.isSelectedVersaoAfterBlur = true;
    }
  }

  resetAlteracao() {
    if (
      !this.isLoadingTipoVeiculo &&
      !this.isLoadingMarca &&
      !this.isLoadingAnoModelo &&
      !this.isLoadingModelo &&
      !this.isLoadingVersao
    ) {
      this.isAlteracao = false;
    }
  }

  public get veiculoPlaca(): string {
    return (
      this.form &&
      this.form.get('veiculoPlaca') &&
      this.form.get('veiculoPlaca').value
    );
  }

  public get isVeiculoEmMeuNome(): boolean {
    return (
      this.form &&
      this.form.get('isVeiculoEmMeuNome') &&
      this.form.get('isVeiculoEmMeuNome').value
    );
  }

  public get isNaoSeiPlaca(): boolean {
    return (
      this.form &&
      this.form.get('isNaoSeiPlaca') &&
      this.form.get('isNaoSeiPlaca').value
    );
  }

  public get veiculoCategoria(): string {
    return (
      this.form &&
      this.form.get('veiculoCategoria') &&
      this.form.get('veiculoCategoria').value
    );
  }

  public get veiculoMarca(): string {
    return (
      this.form &&
      this.form.get('veiculoMarca') &&
      this.form.get('veiculoMarca').value
    );
  }

  public get veiculoAno(): string {
    return (
      this.form &&
      this.form.get('veiculoAno') &&
      this.form.get('veiculoAno').value
    );
  }

  public get veiculoModelo(): string {
    return (
      this.form &&
      this.form.get('veiculoModelo') &&
      this.form.get('veiculoModelo').value
    );
  }

  public get veiculoVersao(): string {
    return (
      this.form &&
      this.form.get('veiculoVersao') &&
      this.form.get('veiculoVersao').value
    );
  }

  public get formValid(): boolean {
    const {
      isVeiculoEmMeuNome,
      isNaoSeiPlaca,
      veiculoCategoria,
      veiculoMarca,
      veiculoAno,
      veiculoModelo,
      veiculoVersao,
    } = this.form.getRawValue();

    if (isNaoSeiPlaca) {
      return (
        veiculoCategoria &&
        veiculoMarca &&
        veiculoAno &&
        veiculoModelo &&
        veiculoVersao
      );
    }

    if (isVeiculoEmMeuNome) {
      return this.form.get('veiculoPlaca').valid;
    }

    return true;
  }

  public onClickNaoSeiPlaca(): void {
    this.form.get('veiculoPlaca').setValue('');
  }

  onSelectedTipoVeiculoChange() {
    if (this.isSelectedTipoVeiculo) {
      this.form.get('veiculoMarca')?.enable();
    } else {
      this.form.get('veiculoMarca')?.disable();
    }
  }

  onSelectedMarcaChange() {
    if (this.isSelectedMarca) {
      this.form.get('veiculoAno')?.enable();
    } else {
      this.form.get('veiculoAno')?.disable();
    }
  }

  onSelectedAnoModeloChange() {
    if (this.isSelectedAnoModelo) {
      this.form.get('veiculoModelo')?.enable();
    } else {
      this.form.get('veiculoModelo')?.disable();
    }
  }

  onSelectedModeloChange() {
    if (this.isSelectedModelo) {
      this.form.get('veiculoVersao')?.enable();
    } else {
      this.form.get('veiculoVersao')?.disable();
    }
  }

}
